import { SETTINGS_SECTIONS } from '../../app/terminology/settings';
import {
    Badge,
    Banner,
    BlockStack,
    Button,
    Card,
    Checkbox,
    EmptyState,
    FormLayout,
    Icon,
    InlineStack,
    Link,
    Select,
    Spinner,
    Text,
    TextField,
} from "@shopify/polaris";
import { PlusIcon, RefreshIcon } from "@shopify/polaris-icons";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import agent from "../../app/api/agent";
import {
    ShopifyMetafieldAttributeOption,
    ShopifyMetafieldAttributeOwnerType,
    ShopifyMetafieldAttributeUpsert,
    ShopifyMetafieldDefinitionOption,
} from "../../app/models/ShopifyMetafieldAttribute";
import productAttributesEmptyStateImage from "../../assets/dashboard/polaris-attributes.svg";
import { SettingsCard } from "./SettingsControls";
import "./MetafieldAttributesSettings.css";

export type MetafieldAttributesEditorStatus = {
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

type MetafieldAttributesSettingsProps = {
    onStatusChange?: (status: MetafieldAttributesEditorStatus) => void;
    onOpenShopifySync?: () => void;
    onHeaderActionsChange?: (content: ReactNode | null) => void;
};

type DraftAttribute = Omit<ShopifyMetafieldAttributeOption, "id"> & {
    id?: number | null;
};

type AttributeForm = {
    id?: number | null;
    ownerType: ShopifyMetafieldAttributeOwnerType;
    namespace: string;
    key: string;
    name: string;
    type: string;
    shopifyDefinitionId: string;
    description: string;
    isEnabled: boolean;
    displayOrder: string;
};

type AttributeEditorMode = "add" | "edit";

const emptyForm: AttributeForm = {
    id: null,
    ownerType: "ProductVariant",
    namespace: "",
    key: "",
    name: "",
    type: "single_line_text_field",
    shopifyDefinitionId: "",
    description: "",
    isEnabled: true,
    displayOrder: "0",
};

const ownerTypeOptions = [
    { label: "Variant metafield", value: "ProductVariant" },
    { label: "Product metafield", value: "Product" },
];

const ownerTypeLabel = (ownerType: ShopifyMetafieldAttributeOwnerType) =>
    ownerType === "ProductVariant" ? "Variant metafield" : "Product metafield";

const pathKey = (ownerType: ShopifyMetafieldAttributeOwnerType, namespace: string, key: string) =>
    `${ownerType}|${namespace.trim().toLowerCase()}|${key.trim().toLowerCase()}`;

const attributeIdentity = (attribute: DraftAttribute) =>
    attribute.id ? `id:${attribute.id}` : `path:${pathKey(attribute.ownerType, attribute.namespace, attribute.key)}`;

const formatDateTime = (value?: string | null) => {
    if (!value) return "Not synced";

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
};

const cloneAttributes = (attributes: DraftAttribute[]): DraftAttribute[] =>
    attributes.map((attribute) => ({
        ...attribute,
        values: attribute.values ? [...attribute.values] : [],
    }));

const normalizeAttributeSnapshot = (attribute: DraftAttribute) => ({
    id: attribute.id ?? null,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace.trim(),
    key: attribute.key.trim(),
    name: attribute.name.trim(),
    type: attribute.type?.trim() || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: !!attribute.isEnabled,
    displayOrder: Number(attribute.displayOrder) || 0,
});

const attributeSnapshot = (attributes: DraftAttribute[]) => JSON.stringify(
    attributes
        .map(normalizeAttributeSnapshot)
        .sort((left, right) =>
            (left.id ?? 0) - (right.id ?? 0)
            || left.ownerType.localeCompare(right.ownerType)
            || left.namespace.localeCompare(right.namespace)
            || left.key.localeCompare(right.key)
        )
);

const attributeToForm = (attribute: DraftAttribute): AttributeForm => ({
    id: attribute.id,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace,
    key: attribute.key,
    name: attribute.name,
    type: attribute.type || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: attribute.isEnabled,
    displayOrder: String(attribute.displayOrder ?? 0),
});

const formToPayload = (form: AttributeForm): ShopifyMetafieldAttributeUpsert => ({
    id: form.id,
    ownerType: form.ownerType,
    namespace: form.namespace.trim(),
    key: form.key.trim(),
    name: form.name.trim() || `${form.namespace.trim()}.${form.key.trim()}`,
    type: form.type.trim() || "single_line_text_field",
    shopifyDefinitionId: form.shopifyDefinitionId.trim(),
    description: form.description.trim(),
    isEnabled: form.isEnabled,
    displayOrder: Number.parseInt(form.displayOrder, 10) || 0,
});

const attributeToPayload = (attribute: DraftAttribute): ShopifyMetafieldAttributeUpsert => ({
    id: attribute.id,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace.trim(),
    key: attribute.key.trim(),
    name: attribute.name.trim() || `${attribute.namespace.trim()}.${attribute.key.trim()}`,
    type: attribute.type?.trim() || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: attribute.isEnabled,
    displayOrder: Number(attribute.displayOrder) || 0,
});

const payloadToDraftAttribute = (
    payload: ShopifyMetafieldAttributeUpsert,
    existingAttribute?: DraftAttribute
): DraftAttribute => ({
    id: payload.id ?? existingAttribute?.id ?? null,
    ownerType: payload.ownerType,
    namespace: payload.namespace,
    key: payload.key,
    name: payload.name,
    type: payload.type || "single_line_text_field",
    shopifyDefinitionId: payload.shopifyDefinitionId || "",
    description: payload.description || "",
    isEnabled: payload.isEnabled,
    displayOrder: payload.displayOrder,
    lastIndexedAt: existingAttribute?.lastIndexedAt,
    valueCount: existingAttribute?.valueCount ?? 0,
    values: existingAttribute?.values ?? [],
});

const definitionToDraftAttribute = (
    definition: ShopifyMetafieldDefinitionOption,
    existingAttribute: DraftAttribute | undefined,
    displayOrder: number
): DraftAttribute => ({
    id: existingAttribute?.id ?? null,
    ownerType: definition.ownerType,
    namespace: definition.namespace,
    key: definition.key,
    name: definition.name || `${definition.namespace}.${definition.key}`,
    type: definition.type || "single_line_text_field",
    shopifyDefinitionId: definition.shopifyDefinitionId || "",
    description: definition.description || "",
    isEnabled: true,
    displayOrder: existingAttribute?.displayOrder ?? displayOrder,
    lastIndexedAt: existingAttribute?.lastIndexedAt,
    valueCount: existingAttribute?.valueCount ?? 0,
    values: existingAttribute?.values ?? [],
});

const upsertDraftAttribute = (attributes: DraftAttribute[], nextAttribute: DraftAttribute) => {
    let replaced = false;
    const nextPath = pathKey(nextAttribute.ownerType, nextAttribute.namespace, nextAttribute.key);

    const nextAttributes = attributes.map((attribute) => {
        const sameId = !!nextAttribute.id && attribute.id === nextAttribute.id;
        const sameNewPath = !nextAttribute.id && pathKey(attribute.ownerType, attribute.namespace, attribute.key) === nextPath;

        if (!sameId && !sameNewPath) return attribute;

        replaced = true;
        return {
            ...attribute,
            ...nextAttribute,
            values: nextAttribute.values ?? attribute.values ?? [],
        };
    });

    if (!replaced) {
        nextAttributes.push(nextAttribute);
    }

    return nextAttributes.sort((left, right) =>
        (left.displayOrder ?? 0) - (right.displayOrder ?? 0)
        || (left.name || "").localeCompare(right.name || "")
    );
};

const MetafieldAttributesSettings: React.FC<MetafieldAttributesSettingsProps> = ({ onStatusChange, onOpenShopifySync, onHeaderActionsChange }) => {
    const [attributes, setAttributes] = useState<DraftAttribute[]>([]);
    const [baselineAttributes, setBaselineAttributes] = useState<DraftAttribute[]>([]);
    const [definitions, setDefinitions] = useState<ShopifyMetafieldDefinitionOption[]>([]);
    const [form, setForm] = useState<AttributeForm>(emptyForm);
    const [selectedDefinitionKey, setSelectedDefinitionKey] = useState("");
    const [attributeEditorOpen, setAttributeEditorOpen] = useState(false);
    const [attributeEditorMode, setAttributeEditorMode] = useState<AttributeEditorMode>("add");
    const [manualOpen, setManualOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [definitionError, setDefinitionError] = useState<string | undefined>(undefined);
    const [success, setSuccess] = useState<string | undefined>(undefined);
    const [editingIdentity, setEditingIdentity] = useState<string>();
    const [loadFailed, setLoadFailed] = useState(false);
    const editorRef = useRef<HTMLElement>(null);
    const returnFocusRef = useRef<HTMLElement | null>(null);

    const loadData = useCallback(async () => {
        setLoading(true);

        try {
            let definitionsFailed = false;
            const definitionsRequest = agent.ShopifyMetafieldAttributes.definitions().catch(() => {
                definitionsFailed = true;
                return [] as ShopifyMetafieldDefinitionOption[];
            });

            const [loadedAttributes, loadedDefinitions] = await Promise.all([
                agent.ShopifyMetafieldAttributes.list({ enabledOnly: false, includeValues: false }),
                definitionsRequest,
            ]);

            const nextAttributes = cloneAttributes(loadedAttributes);
            setAttributes(nextAttributes);
            setBaselineAttributes(cloneAttributes(nextAttributes));
            setDefinitions(loadedDefinitions);
            setDefinitionError(definitionsFailed ? "Existing Shopify metafields could not be loaded. Use manual linking if needed." : undefined);
            setError(undefined);
            setLoadFailed(false);
            return true;
        } catch {
            setLoadFailed(true);
            setError("Attributes could not be loaded.");
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const hasUnsavedChanges = useMemo(
        () => attributeSnapshot(attributes) !== attributeSnapshot(baselineAttributes),
        [attributes, baselineAttributes]
    );

    const enabledPathKeys = useMemo(
        () => new Set(attributes.filter((attribute) => attribute.isEnabled).map((attribute) => pathKey(attribute.ownerType, attribute.namespace, attribute.key))),
        [attributes]
    );

    const attributeByPath = useMemo(() => {
        const map = new Map<string, DraftAttribute>();
        attributes.forEach((attribute) => {
            map.set(pathKey(attribute.ownerType, attribute.namespace, attribute.key), attribute);
        });
        return map;
    }, [attributes]);

    const baselineByIdentity = useMemo(() => {
        const map = new Map<string, string>();
        baselineAttributes.forEach((attribute) => {
            map.set(attributeIdentity(attribute), JSON.stringify(normalizeAttributeSnapshot(attribute)));
        });
        return map;
    }, [baselineAttributes]);

    const definitionOptions = useMemo(
        () => [
            { label: definitions.length === 0 ? "No Shopify metafields found" : "Select a Shopify metafield", value: "" },
            ...ownerTypeOptions.map((ownerType) => ({
                title: `${ownerType.label}s`,
                options: definitions
                    .filter((definition) => definition.ownerType === ownerType.value)
                    .map((definition) => {
                        const key = pathKey(definition.ownerType, definition.namespace, definition.key);
                        const enabled = enabledPathKeys.has(key);
                        return {
                            label: `${definition.name || `${definition.namespace}.${definition.key}`}${enabled ? " (enabled)" : ""}`,
                            value: key,
                        };
                    }),
            })).filter((group) => group.options.length > 0),
        ],
        [definitions, enabledPathKeys]
    );

    const selectedDefinition = definitions.find(
        (definition) => pathKey(definition.ownerType, definition.namespace, definition.key) === selectedDefinitionKey
    );
    const selectedDefinitionExistingAttribute = selectedDefinition ? attributeByPath.get(selectedDefinitionKey) : undefined;
    const selectedDefinitionAlreadyEnabled = !!selectedDefinitionExistingAttribute?.isEnabled;
    const enabledAttributeCount = attributes.filter((attribute) => attribute.isEnabled).length;
    const hasAttributes = attributes.length > 0;
    const nextDisplayOrder = Math.max(0, ...attributes.map((attribute) => attribute.displayOrder ?? 0)) + 10;

    const closeEditor = useCallback(() => {
        setAttributeEditorOpen(false);
        setAttributeEditorMode("add");
        setSelectedDefinitionKey("");
        setManualOpen(false);
        setForm(emptyForm);
        setError(undefined);
        setEditingIdentity(undefined);
        requestAnimationFrame(() => {
            const target = returnFocusRef.current;
            if (target?.isConnected) target.focus();
            else document.getElementById("connect-product-attribute")?.focus();
        });
    }, []);

    const openAddAttributeEditor = useCallback(() => {
        returnFocusRef.current = document.activeElement as HTMLElement | null;
        setEditingIdentity(undefined);
        setAttributeEditorMode("add");
        setForm({ ...emptyForm, displayOrder: String(nextDisplayOrder) });
        setSelectedDefinitionKey("");
        setManualOpen(false);
        setAttributeEditorOpen(true);
        setError(undefined);
        setSuccess(undefined);
    }, [nextDisplayOrder]);

    const openEditAttributeEditor = useCallback((attribute: DraftAttribute) => {
        returnFocusRef.current = document.activeElement as HTMLElement | null;
        setEditingIdentity(attributeIdentity(attribute));
        setAttributeEditorMode("edit");
        setForm(attributeToForm(attribute));
        setSelectedDefinitionKey("");
        setManualOpen(true);
        setAttributeEditorOpen(true);
        setError(undefined);
        setSuccess(undefined);
    }, []);

    useEffect(() => {
        if (!attributeEditorOpen) return;
        editorRef.current?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled])')?.focus();
        editorRef.current?.scrollIntoView?.({ block: "nearest" });
    }, [attributeEditorOpen]);

    const toggleAttribute = useCallback((attribute: DraftAttribute, isEnabled: boolean) => {
        setSuccess(undefined);
        setAttributes((current) => current.map((currentAttribute) =>
            attributeIdentity(currentAttribute) === attributeIdentity(attribute)
                ? { ...currentAttribute, isEnabled }
                : currentAttribute
        ));
    }, []);

    const addSelectedDefinitionToDraft = useCallback(() => {
        if (!selectedDefinition) {
            setError("Select a Shopify metafield first.");
            return;
        }

        if (selectedDefinitionAlreadyEnabled) {
            setError("That metafield is already connected.");
            return;
        }

        const nextAttribute = definitionToDraftAttribute(selectedDefinition, selectedDefinitionExistingAttribute, nextDisplayOrder);
        setAttributes((current) => upsertDraftAttribute(current, nextAttribute));
        closeEditor();
    }, [
        nextDisplayOrder,
        closeEditor,
        selectedDefinition,
        selectedDefinitionAlreadyEnabled,
        selectedDefinitionExistingAttribute,
    ]);

    const saveManualAttributeToDraft = useCallback(() => {
        const payload = formToPayload(form);
        if (!payload.namespace || !payload.key) {
            setError("Namespace and key are required.");
            return;
        }

        const duplicate = attributes.find((attribute) =>
            attributeIdentity(attribute) !== editingIdentity
            && pathKey(attribute.ownerType, attribute.namespace, attribute.key) === pathKey(payload.ownerType, payload.namespace, payload.key)
        );

        if (duplicate) {
            setError("That metafield is already linked as an attribute.");
            return;
        }

        const existingAttribute = editingIdentity
            ? attributes.find((attribute) => attributeIdentity(attribute) === editingIdentity)
            : attributeByPath.get(pathKey(payload.ownerType, payload.namespace, payload.key));

        setAttributes((current) => upsertDraftAttribute(
            editingIdentity ? current.filter(attribute => attributeIdentity(attribute) !== editingIdentity) : current,
            payloadToDraftAttribute(payload, existingAttribute)
        ));
        closeEditor();
    }, [attributeByPath, attributes, editingIdentity, form, closeEditor]);

    const applyEditorToDraft = useCallback(() => {
        if (attributeEditorMode === "edit" || manualOpen) {
            saveManualAttributeToDraft();
            return;
        }

        addSelectedDefinitionToDraft();
    }, [addSelectedDefinitionToDraft, attributeEditorMode, manualOpen, saveManualAttributeToDraft]);

    const discardChanges = useCallback(() => {
        setAttributes(cloneAttributes(baselineAttributes));
        closeEditor();
        setError(undefined);
        setSuccess(undefined);
    }, [baselineAttributes, closeEditor]);

    const saveChanges = useCallback(async () => {
        if (!hasUnsavedChanges) return;

        const changedAttributes = attributes.filter((attribute) =>
            baselineByIdentity.get(attributeIdentity(attribute)) !== JSON.stringify(normalizeAttributeSnapshot(attribute))
        );

        setSaving(true);
        setError(undefined);
        setSuccess(undefined);

        try {
            for (const attribute of changedAttributes) {
                await agent.ShopifyMetafieldAttributes.upsert(attributeToPayload(attribute));
            }

            if (await loadData()) setSuccess("Product attributes saved.");
        } catch {
            setError("Product attributes could not be saved.");
        } finally {
            setSaving(false);
        }
    }, [attributes, baselineByIdentity, hasUnsavedChanges, loadData]);

    useEffect(() => {
        onStatusChange?.({
            hasChanges: hasUnsavedChanges,
            loading,
            saving,
            save: saveChanges,
            discard: discardChanges,
        });
    }, [discardChanges, hasUnsavedChanges, loading, onStatusChange, saveChanges, saving]);

    const editorPrimaryLabel = attributeEditorMode === "edit" ? "Apply to draft" : "Add to draft";

    const editorPrimaryDisabled = saving
        || (attributeEditorMode === "add" && !manualOpen && (!selectedDefinition || selectedDefinitionAlreadyEnabled))
        || ((attributeEditorMode === "edit" || manualOpen) && (!form.namespace.trim() || !form.key.trim()));

    const headerActions = useMemo(() => (
        <Button id="connect-product-attribute" variant="primary" icon={PlusIcon} onClick={openAddAttributeEditor} disabled={loading || saving || attributeEditorOpen || loadFailed}>
            Connect metafield
        </Button>
    ), [attributeEditorOpen, loadFailed, loading, openAddAttributeEditor, saving]);

    useEffect(() => {
        if (!onHeaderActionsChange) return;
        onHeaderActionsChange(headerActions);
        return () => onHeaderActionsChange(null);
    }, [headerActions, onHeaderActionsChange]);

    const editingAttribute = attributes.find(attribute => attributeIdentity(attribute) === editingIdentity);
    const connectionFields = (
        <FormLayout>
            <Select label="Owner" options={ownerTypeOptions} value={form.ownerType}
                onChange={(value) => setForm((current) => ({ ...current, ownerType: value as ShopifyMetafieldAttributeOwnerType }))} />
            <FormLayout.Group>
                <TextField label="Namespace" value={form.namespace} onChange={(value) => setForm((current) => ({ ...current, namespace: value }))} autoComplete="off" />
                <TextField label="Key" value={form.key} onChange={(value) => setForm((current) => ({ ...current, key: value }))} autoComplete="off" />
            </FormLayout.Group>
            <TextField label="Display order" value={form.displayOrder} onChange={(value) => setForm((current) => ({ ...current, displayOrder: value }))} autoComplete="off" type="number" />
        </FormLayout>
    );

    if (loading && attributes.length === 0) {
        return (
            <SettingsCard title={SETTINGS_SECTIONS.productAttributes}>
                <InlineStack gap="200" align="center">
                    <Spinner accessibilityLabel="Loading product attributes" size="small" />
                    <Text as="span">Loading product attributes...</Text>
                </InlineStack>
            </SettingsCard>
        );
    }

    return (
        <div className="pf-attributes">
            {!onHeaderActionsChange && (hasAttributes || attributeEditorOpen) && <div className="pf-attributes__actions">{headerActions}</div>}
            {error && !attributeEditorOpen && <Banner tone="critical">{error}</Banner>}
            {success && <Banner tone="success">{success}</Banner>}
            {loadFailed && <InlineStack><Button onClick={loadData} loading={loading}>Retry loading attributes</Button></InlineStack>}

            {attributeEditorOpen && (
                <section ref={editorRef} className="pf-attributes__editor" aria-labelledby="pf-attribute-editor-title"
                    onKeyDown={event => {
                        if (event.key === "Escape" && !saving) { event.preventDefault(); event.stopPropagation(); closeEditor(); }
                    }}>
                    <Card padding="0">
                        <div className="pf-attributes__editor-heading">
                            <Text as="h2" variant="headingMd" id="pf-attribute-editor-title">
                                {attributeEditorMode === "edit" ? `Edit ${editingAttribute?.name || "attribute"}` : "Connect metafield"}
                            </Text>
                            <Text as="span" variant="bodySm" tone="subdued">Changes apply to your draft</Text>
                        </div>
                        <fieldset className="pf-attributes__editor-body" disabled={saving}>
                            <BlockStack gap="400">
                                {error && <Banner tone="critical">{error}</Banner>}
                                {definitionError && attributeEditorMode === "add" && <Banner tone="warning">{definitionError}</Banner>}
                                {attributeEditorMode === "add" && (
                                    <>
                                        {!manualOpen && <Select
                                            label="Existing Shopify metafield" options={definitionOptions} value={selectedDefinitionKey}
                                            onChange={setSelectedDefinitionKey} disabled={definitions.length === 0 || saving} />}
                                        {selectedDefinition && !manualOpen && (
                                            <div className="pf-attributes__metadata">
                                                <Text as="p" fontWeight="semibold">{selectedDefinition.namespace}.{selectedDefinition.key}</Text>
                                                <Text as="p" variant="bodySm" tone="subdued">{ownerTypeLabel(selectedDefinition.ownerType)}</Text>
                                                {selectedDefinition.description && <Text as="p" variant="bodySm" tone="subdued">{selectedDefinition.description}</Text>}
                                            </div>
                                        )}
                                        <InlineStack gap="300" blockAlign="center">
                                            <Button variant="plain" onClick={() => setManualOpen(open => !open)} disabled={saving} ariaExpanded={manualOpen} ariaControls="manual-attribute-link">
                                                {manualOpen ? "Use metafield picker" : "Enter manually"}
                                            </Button>
                                            <Text as="span" variant="bodySm" tone="subdued">For metafields that don’t appear in the picker.</Text>
                                        </InlineStack>
                                    </>
                                )}

                                {editingAttribute && (
                                    <div className="pf-attributes__metadata">
                                        <Text as="p" fontWeight="semibold">{ownerTypeLabel(editingAttribute.ownerType)} · {editingAttribute.namespace}.{editingAttribute.key}</Text>
                                        <Text as="p" variant="bodySm" tone="subdued">{editingAttribute.valueCount} cached {editingAttribute.valueCount === 1 ? "value" : "values"} · Last synced: {formatDateTime(editingAttribute.lastIndexedAt)}</Text>
                                        {editingAttribute.valueCount === 0 && <Text as="p" variant="bodySm" tone="subdued">No cached values are available for this attribute.</Text>}
                                    </div>
                                )}

                                <div id="manual-attribute-link" hidden={!manualOpen && attributeEditorMode !== "edit"}>
                                    <FormLayout>
                                        {attributeEditorMode === "add" && connectionFields}
                                        <TextField label="Display name" value={form.name} onChange={(value) => setForm((current) => ({ ...current, name: value }))} autoComplete="off" />
                                        <TextField label="Description" value={form.description} onChange={(value) => setForm((current) => ({ ...current, description: value }))} autoComplete="off" multiline={3} />
                                        <Checkbox label="Use in app" checked={form.isEnabled} onChange={(checked) => setForm((current) => ({ ...current, isEnabled: checked }))} disabled={saving} />
                                        {attributeEditorMode === "edit" && (
                                            <details className="pf-attributes__connection-details">
                                                <summary>Connection details &amp; display order</summary>
                                                {connectionFields}
                                            </details>
                                        )}
                                    </FormLayout>
                                </div>
                            </BlockStack>
                        </fieldset>
                        <div className="pf-attributes__editor-footer">
                            <Button onClick={closeEditor} disabled={saving}>Cancel</Button>
                            <Button variant="primary" onClick={applyEditorToDraft} disabled={editorPrimaryDisabled}>
                                {selectedDefinitionAlreadyEnabled && attributeEditorMode === "add" && !manualOpen ? "Already connected" : editorPrimaryLabel}
                            </Button>
                        </div>
                    </Card>
                </section>
            )}

            {!hasAttributes ? (!loadFailed && !attributeEditorOpen && (
                <Card>
                    <EmptyState heading="No product attributes connected yet" image={productAttributesEmptyStateImage}
                        action={onHeaderActionsChange ? undefined : { content: "Connect metafield", onAction: openAddAttributeEditor, disabled: saving }}>
                        <p>Connect Shopify metafields to use their values in Product Guides, filters, and storefront displays.</p>
                    </EmptyState>
                </Card>
            )) : (
                <section aria-labelledby="pf-connected-attributes-title">
                    <Card padding="0">
                        <div className="pf-attributes__list-heading">
                            <InlineStack gap="200" blockAlign="center">
                                <Text as="h2" variant="headingSm" id="pf-connected-attributes-title">Connected attributes</Text>
                                <Badge>{String(attributes.length)}</Badge>
                            </InlineStack>
                            <Text as="span" variant="bodySm" tone="subdued">{enabledAttributeCount} enabled</Text>
                        </div>
                        <div className="pf-attributes__columns pf-attributes__grid" aria-hidden="true">
                            <span>Attribute / metafield</span><span>Cached values</span><span>Use in app</span><span />
                        </div>
                        <ul className="pf-attributes__rows" aria-label="Connected attributes">
                            {attributes.map(attribute => {
                                const name = attribute.name || `${attribute.namespace}.${attribute.key}`;
                                return (
                                    <li key={attributeIdentity(attribute)} className="pf-attributes__row pf-attributes__grid">
                                        <div className="pf-attributes__attribute">
                                            <Text as="p" fontWeight="semibold">{name}</Text>
                                            <div className="pf-attributes__source">
                                                <span className="pf-attributes__scope">{attribute.ownerType === "ProductVariant" ? "Variant" : "Product"}</span>
                                                <Text as="span" variant="bodySm" tone="subdued">{attribute.namespace}.{attribute.key}</Text>
                                            </div>
                                        </div>
                                        <div className="pf-attributes__values">
                                            {attribute.valueCount > 0
                                                ? <Text as="span" variant="bodySm" tone="subdued">{attribute.valueCount} {attribute.valueCount === 1 ? "value" : "values"}</Text>
                                                : <Badge>{attribute.lastIndexedAt ? "No values" : "Not synced"}</Badge>}
                                        </div>
                                        <label className="pf-attributes__toggle-target">
                                            <input className="pf-attributes__switch" type="checkbox" role="switch" aria-label={`Use ${name} in app`}
                                                checked={attribute.isEnabled} disabled={saving || loading || attributeEditorOpen}
                                                onChange={event => toggleAttribute(attribute, event.target.checked)} />
                                        </label>
                                        <div className="pf-attributes__edit">
                                            <Button variant="plain" accessibilityLabel={`Edit ${name}`} onClick={() => openEditAttributeEditor(attribute)} disabled={saving || loading || attributeEditorOpen}>Edit</Button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="pf-attributes__footer"><Text as="p" variant="bodySm" tone="subdued">Only connected metafields are synced to PartFits.</Text></div>
                    </Card>
                </section>
            )}
            {!loadFailed && <div className="pf-attributes__sync">
                <Icon source={RefreshIcon} tone="subdued" />
                <Text as="p" variant="bodySm" tone="subdued">Need updated values? {onOpenShopifySync
                    ? <Link onClick={onOpenShopifySync}>Shopify sync</Link>
                    : "Open Shopify sync to refresh attribute values."}</Text>
            </div>}
        </div>
    );
};

export default MetafieldAttributesSettings;
